import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface HelpResourceItemContent {
  icon: IconName;
  title: ReactNode;
  description: ReactNode;
  isInternal?: boolean;
}

interface HelpResourceItemProps {
  content: HelpResourceItemContent;
  onClick: () => void;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StyledButton = styled(ChromelessButton)`
  padding-left: 8px;
  cursor: pointer;

  * {
    cursor: inherit;
  }
`;

const StyledLinkIcon = styled(Icon)`
  display: inline;

  ${hoverSelector} {
    color: ${(p) => p.theme.palette.blue.shade40};
  }
`;

const StyledTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  padding-bottom: 4px;

  ${hoverSelector} {
    color: ${(p) => p.theme.palette.blue.shade40};
  }
`;

const StyledDescription = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 131%;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const HelpResourceItem: FC<HelpResourceItemProps> = (props) => {
  const {content, onClick} = props;
  const {icon, title, description, isInternal} = content;
  const theme = useUserTheme();
  return (
    <StyledDiv>
      <Icon name={icon} color={theme.greys.shade80} />
      <StyledButton onClick={onClick}>
        <StyledTitle>
          {title}
          {!isInternal && (
            <StyledLinkIcon
              name="externalLinkSmall"
              color={theme.greys.shade80}
              hoverColor={theme.palette.blue.shade40}
            />
          )}
        </StyledTitle>
        <StyledDescription>{description}</StyledDescription>
      </StyledButton>
    </StyledDiv>
  );
};
